import React from "react";
import { useTable, Column, useSortBy } from "react-table";
import { BsPlusCircle, BsPencil, BsTrash, BsSortDown, BsSortUp } from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";
import moment from "moment";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { ApiKey } from "../../../../models";
import {
  getApiKeys,
  openCreateModal,
  setEditingKey,
  setRevokingKey,
} from "../../apiKeysSlice";
import {
  CreateApiKeyModal,
  RevealApiKeyModal,
  EditApiKeyModal,
  RevokeApiKeyConfirmModal,
} from "./modals";

import "./ApiKeysTab.scss";

export function ApiKeysTab() {
  const dispatch = useAppDispatch();
  const apiKeys = useAppSelector((state) => state.apiKeys.apiKeys) || [];
  const getApiKeysAPI = useAppSelector((state) => state.apiKeys.getApiKeys);

  React.useEffect(() => {
    dispatch(getApiKeys());
  }, [dispatch]);

  React.useEffect(() => {
    if (!getApiKeysAPI.loading && getApiKeysAPI.error) {
      toast.error("Failed to load API keys. Please try again.");
    }
  }, [getApiKeysAPI.loading, getApiKeysAPI.error]);

  const columns: Array<Column<ApiKey>> = React.useMemo(
    () => [
      { Header: "Name", accessor: "name" },
      {
        Header: "Key",
        accessor: "keyPrefix",
        Cell: ({ value }) => <code className="key-prefix">{value}…</code>,
      },
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ value }) => <span className="role-badge">{value}</span>,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <span>{moment(value).format("MMM D, YYYY")}</span>,
      },
      {
        Header: "Last used",
        accessor: "lastUsedAt",
        Cell: ({ value }) => <span>{value ? moment(value).format("MMM D, YYYY") : "Never"}</span>,
      },
      {
        Header: "Status",
        accessor: "revokedAt",
        Cell: ({ value }) => (
          <span className={`status-badge ${value ? "revoked" : "active"}`}>
            {value ? "Revoked" : "Active"}
          </span>
        ),
      },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }: { row: { original: ApiKey } }) => {
          const apiKey = row.original;
          if (apiKey.revokedAt) return <span className="muted">—</span>;
          return (
            <div className="actions-cell">
              <BsPencil
                className="edit-icon"
                onClick={() => dispatch(setEditingKey(apiKey))}
              />
              <BsTrash
                className="delete-icon"
                onClick={() => dispatch(setRevokingKey(apiKey))}
              />
            </div>
          );
        },
      },
    ],
    [dispatch]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: apiKeys }, useSortBy);

  return (
    <div className="tab-content api-keys-tab">
      <div className="tab-header">
        <h2>API Keys</h2>
        <button className="add-button" onClick={() => dispatch(openCreateModal())}>
          <BsPlusCircle className="icon" />
          Create API Key
        </button>
      </div>

      {getApiKeysAPI.loading && apiKeys.length === 0 ? (
        <div className="loading-container">
          <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
        </div>
      ) : apiKeys.length === 0 ? (
        <p className="empty-state">No API keys yet. Create one to get started.</p>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="tp-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps((column as any).getSortByToggleProps())}>
                      <div className="header-cell">
                        {column.render("Header")}
                        {(column as any).isSorted ? (
                          (column as any).isSortedDesc ? (
                            <BsSortDown className="sorting-icon" />
                          ) : (
                            <BsSortUp className="sorting-icon" />
                          )
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateApiKeyModal />
      <RevealApiKeyModal />
      <EditApiKeyModal />
      <RevokeApiKeyConfirmModal />
    </div>
  );
}
